import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import {
  Code,
  Play,
  Copy,
  CheckCircle,
  AlertCircle,
  ChevronRight,
  ChevronDown,
  Key,
  Send,
  MessageSquare,
  Users,
  FileText,
  Bell,
  Search,
  ExternalLink,
  Zap,
  Shield,
  Clock
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// API Endpoints documentation
const API_ENDPOINTS = [
  {
    category: 'Messages',
    icon: Send,
    color: 'indigo',
    endpoints: [
      {
        method: 'POST',
        path: '/api/v1/messages',
        summary: 'Envoyer un message RCS',
        description: 'Envoie un message RCS à un destinataire. Supporte tous les types: BASIC, TEXT, CARD, CAROUSEL.',
        requestBody: {
          recipient: { to: '+33612345678' },
          body: { type: 'TEXT', text: 'Bonjour ! Ceci est un test.' },
          fallbackSms: true,
          refClient: 'ORDER-123'
        },
        responses: {
          201: { description: 'Message accepté', example: { messageId: 'uuid', status: { value: 'ENROUTE' } } },
          400: { description: 'Requête invalide' },
          401: { description: 'Clé API invalide' }
        }
      },
      {
        method: 'POST',
        path: '/api/v1/messages/batch',
        summary: 'Envoi en masse (batch)',
        description: 'Envoie jusqu\'à 3072 messages en une seule requête.',
        requestBody: {
          messages: [
            { recipient: { to: '+33612345678' }, body: { type: 'TEXT', text: 'Message 1' } },
            { recipient: { to: '+33698765432' }, body: { type: 'TEXT', text: 'Message 2' } }
          ]
        }
      },
      {
        method: 'GET',
        path: '/api/v1/messages',
        summary: 'Lister les messages',
        description: 'Retourne la liste paginée des messages avec filtres.',
        parameters: [
          { name: 'page', type: 'integer', description: 'Numéro de page' },
          { name: 'pageSize', type: 'integer', description: 'Taille de page (max 100)' },
          { name: 'searchBy[status]', type: 'string', description: 'Filtrer par statut' }
        ]
      },
      {
        method: 'GET',
        path: '/api/v1/messages/{messageId}',
        summary: 'Détails d\'un message',
        description: 'Retourne les détails complets d\'un message spécifique.'
      },
      {
        method: 'PATCH',
        path: '/api/v1/messages/{messageId}',
        summary: 'Modifier un message planifié',
        description: 'Modifie un message avec statut SCHEDULED (5min avant envoi minimum).'
      },
      {
        method: 'DELETE',
        path: '/api/v1/messages/{messageId}',
        summary: 'Annuler un message planifié',
        description: 'Annule un message planifié (5min avant envoi minimum).'
      }
    ]
  },
  {
    category: 'Capability Check',
    icon: Shield,
    color: 'green',
    badge: 'Exclusif B2RCS',
    endpoints: [
      {
        method: 'POST',
        path: '/api/v1/capability/check',
        summary: 'Vérifier compatibilité RCS (batch)',
        description: 'Vérifie si une liste de numéros supporte RCS. Max 1000 numéros par requête.',
        requestBody: {
          phones: ['+33612345678', '+33698765432', '+33611111111']
        },
        responses: {
          200: {
            description: 'Résultats de la vérification',
            example: {
              total: 3,
              rcs_enabled_count: 2,
              sms_fallback_count: 1,
              results: [
                { phone: '+33612345678', rcs_enabled: true, carrier: 'Orange' }
              ]
            }
          }
        }
      },
      {
        method: 'GET',
        path: '/api/v1/capability/{phone}',
        summary: 'Vérifier un numéro',
        description: 'Vérifie la compatibilité RCS d\'un seul numéro.'
      }
    ]
  },
  {
    category: 'Templates',
    icon: FileText,
    color: 'purple',
    badge: 'Exclusif B2RCS',
    endpoints: [
      {
        method: 'GET',
        path: '/api/v1/templates/library',
        summary: 'Bibliothèque de templates',
        description: 'Accès aux 42+ templates système par industrie.',
        parameters: [
          { name: 'industry', type: 'string', description: 'Filtrer par industrie (automotive, finance...)' },
          { name: 'category', type: 'string', description: 'Filtrer par catégorie (marketing, notification...)' }
        ]
      },
      {
        method: 'POST',
        path: '/api/v1/templates/{templateId}/render',
        summary: 'Générer depuis template',
        description: 'Génère un message en substituant les variables.',
        requestBody: {
          variables: { prenom: 'Jean', date_rdv: '25 janvier' }
        }
      },
      {
        method: 'GET',
        path: '/api/v1/templates',
        summary: 'Mes templates',
        description: 'Liste les templates personnalisés du client.'
      },
      {
        method: 'POST',
        path: '/api/v1/templates',
        summary: 'Créer un template',
        description: 'Crée un nouveau template personnalisé.'
      }
    ]
  },
  {
    category: 'Webhooks',
    icon: Bell,
    color: 'orange',
    endpoints: [
      {
        method: 'POST',
        path: '/api/v1/webhooks',
        summary: 'Créer un webhook',
        description: 'Configure un endpoint pour recevoir les événements (delivered, read, failed...).',
        requestBody: {
          name: 'Delivery Reports',
          url: 'https://votre-serveur.com/webhook',
          events: ['message.delivered', 'message.read']
        }
      },
      {
        method: 'GET',
        path: '/api/v1/webhooks',
        summary: 'Lister les webhooks',
        description: 'Retourne tous les webhooks configurés.'
      },
      {
        method: 'POST',
        path: '/api/v1/webhooks/{webhookId}/test',
        summary: 'Tester un webhook',
        description: 'Envoie un événement de test à l\'URL configurée.'
      }
    ]
  },
  {
    category: 'Testers',
    icon: Users,
    color: 'cyan',
    badge: 'Nouvelle API Google',
    endpoints: [
      {
        method: 'POST',
        path: '/api/testers',
        summary: 'Inviter un testeur',
        description: 'Envoie une invitation RCS à un numéro pour le devenir testeur.',
        requestBody: {
          phoneNumber: '+33612345678',
          agentId: 'agent-id-xxx'
        }
      },
      {
        method: 'GET',
        path: '/api/testers',
        summary: 'Lister les testeurs',
        description: 'Liste tous les testeurs avec leur statut (PENDING, ACCEPTED, DECLINED).'
      },
      {
        method: 'DELETE',
        path: '/api/testers/{phoneNumber}',
        summary: 'Supprimer un testeur',
        description: 'Supprime un testeur de la liste.'
      },
      {
        method: 'POST',
        path: '/api/testers/{phoneNumber}/resend',
        summary: 'Renvoyer l\'invitation',
        description: 'Renvoie l\'invitation à un testeur existant.'
      }
    ]
  }
];

// Code examples
const CODE_EXAMPLES = {
  python: `import requests

API_KEY = "b2rcs_live_xxxxxxxxxxxxxxxxxxxx"
BASE_URL = "https://api.b2rcs.com/api/v1"

# Envoyer un message
response = requests.post(
    f"{BASE_URL}/messages",
    headers={
        "X-Api-Key": API_KEY,
        "Content-Type": "application/json"
    },
    json={
        "recipient": {"to": "+33612345678"},
        "body": {
            "type": "TEXT",
            "text": "Bonjour ! Ceci est un test RCS."
        },
        "fallbackSms": True
    }
)

print(response.json())`,
  
  nodejs: `const axios = require('axios');

const API_KEY = 'b2rcs_live_xxxxxxxxxxxxxxxxxxxx';
const BASE_URL = 'https://api.b2rcs.com/api/v1';

async function sendMessage() {
  const response = await axios.post(
    \`\${BASE_URL}/messages\`,
    {
      recipient: { to: '+33612345678' },
      body: {
        type: 'TEXT',
        text: 'Bonjour ! Ceci est un test RCS.'
      },
      fallbackSms: true
    },
    {
      headers: {
        'X-Api-Key': API_KEY,
        'Content-Type': 'application/json'
      }
    }
  );
  
  console.log(response.data);
}

sendMessage();`,
  
  php: `<?php
$apiKey = 'b2rcs_live_xxxxxxxxxxxxxxxxxxxx';
$baseUrl = 'https://api.b2rcs.com/api/v1';

$ch = curl_init("$baseUrl/messages");
curl_setopt_array($ch, [
    CURLOPT_POST => true,
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_HTTPHEADER => [
        "X-Api-Key: $apiKey",
        "Content-Type: application/json"
    ],
    CURLOPT_POSTFIELDS => json_encode([
        'recipient' => ['to' => '+33612345678'],
        'body' => [
            'type' => 'TEXT',
            'text' => 'Bonjour ! Ceci est un test RCS.'
        ],
        'fallbackSms' => true
    ])
]);

$response = curl_exec($ch);
curl_close($ch);

print_r(json_decode($response, true));`,
  
  curl: `curl -X POST "https://api.b2rcs.com/api/v1/messages" \\
  -H "X-Api-Key: b2rcs_live_xxxxxxxxxxxxxxxxxxxx" \\
  -H "Content-Type: application/json" \\
  -d '{
    "recipient": {"to": "+33612345678"},
    "body": {
      "type": "TEXT",
      "text": "Bonjour ! Ceci est un test RCS."
    },
    "fallbackSms": true
  }'`
};

const METHOD_COLORS = {
  GET: 'bg-green-100 text-green-700',
  POST: 'bg-blue-100 text-blue-700',
  PUT: 'bg-yellow-100 text-yellow-700',
  PATCH: 'bg-orange-100 text-orange-700',
  DELETE: 'bg-red-100 text-red-700'
};

const APIDeveloper = ({ embedded = false }) => {
  const [selectedEndpoint, setSelectedEndpoint] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(['Messages']);
  const [apiKey, setApiKey] = useState('');
  const [requestBody, setRequestBody] = useState('');
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('curl');
  const [copied, setCopied] = useState(false);

  // Load API key from localStorage
  useEffect(() => {
    const savedKey = localStorage.getItem('b2rcs_api_key');
    if (savedKey) setApiKey(savedKey);
  }, []);

  const toggleCategory = (category) => {
    setExpandedCategories(prev => 
      prev.includes(category) 
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };

  const selectEndpoint = (endpoint) => {
    setSelectedEndpoint(endpoint);
    setResponse(null);
    if (endpoint.requestBody) {
      setRequestBody(JSON.stringify(endpoint.requestBody, null, 2));
    } else {
      setRequestBody('');
    }
  };

  const executeRequest = async () => {
    if (!apiKey) {
      toast.error('Veuillez entrer votre clé API');
      return;
    }
    if (!selectedEndpoint) return;

    setLoading(true);
    try {
      const config = {
        method: selectedEndpoint.method.toLowerCase(),
        url: `${API_URL}${selectedEndpoint.path.replace('{messageId}', 'test').replace('{templateId}', 'test').replace('{webhookId}', 'test').replace('{phoneNumber}', '+33600000000').replace('{phone}', '+33600000000')}`,
        headers: {
          'X-Api-Key': apiKey,
          'Content-Type': 'application/json'
        }
      };

      if (requestBody && ['POST', 'PUT', 'PATCH'].includes(selectedEndpoint.method)) {
        config.data = JSON.parse(requestBody);
      }

      const res = await axios(config);
      setResponse({
        status: res.status,
        statusText: res.statusText,
        data: res.data,
        time: Date.now()
      });
      toast.success('Requête exécutée avec succès');
    } catch (error) {
      setResponse({
        status: error.response?.status || 500,
        statusText: error.response?.statusText || 'Error',
        data: error.response?.data || { error: error.message },
        time: Date.now(),
        isError: true
      });
      toast.error(`Erreur ${error.response?.status || ''}: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Copié !');
  };

  const saveApiKey = () => {
    localStorage.setItem('b2rcs_api_key', apiKey);
    toast.success('Clé API sauvegardée');
  };

  const content = (
    <div className="space-y-6" data-testid="api-developer-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">API Développeur</h1>
          <p className="text-slate-600 mt-1">
            Testez et explorez l'API B2RCS en temps réel
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Badge className="bg-green-100 text-green-700">API v1</Badge>
            <Button variant="outline" onClick={() => window.open('/api/docs', '_blank')}>
              <ExternalLink className="h-4 w-4 mr-2" />
              OpenAPI Spec
            </Button>
          </div>
        </div>

        {/* API Key Section */}
        <Card className="bg-gradient-to-r from-slate-900 to-slate-800 text-white">
          <CardContent className="py-4">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-white/10 rounded-lg">
                <Key className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <Label className="text-slate-300 text-sm">Clé API</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    type="password"
                    placeholder="b2rcs_live_xxxxxxxxxxxxxxxxxxxx"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    className="bg-white/10 border-white/20 text-white placeholder:text-slate-400"
                  />
                  <Button onClick={saveApiKey} variant="secondary" size="sm">
                    Sauvegarder
                  </Button>
                </div>
              </div>
              <Button 
                variant="outline" 
                className="border-white/20 text-white hover:bg-white/10"
                onClick={() => window.location.href = '/api-keys'}
              >
                Gérer mes clés
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-12 gap-6">
          {/* Sidebar - Endpoints List */}
          <div className="col-span-4">
            <Card className="sticky top-24">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Code className="h-5 w-5" />
                  Endpoints
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <ScrollArea className="h-[calc(100vh-350px)]">
                  <div className="p-2">
                    {API_ENDPOINTS.map((cat) => {
                      const Icon = cat.icon;
                      const isExpanded = expandedCategories.includes(cat.category);
                      
                      return (
                        <div key={cat.category} className="mb-1">
                          <button
                            onClick={() => toggleCategory(cat.category)}
                            className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors"
                          >
                            <div className="flex items-center gap-2">
                              <Icon className={`h-4 w-4 text-${cat.color}-600`} />
                              <span className="font-medium text-sm">{cat.category}</span>
                              {cat.badge && (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                                  {cat.badge}
                                </Badge>
                              )}
                            </div>
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4 text-slate-400" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-slate-400" />
                            )}
                          </button>
                          
                          {isExpanded && (
                            <div className="ml-6 space-y-1 mt-1">
                              {cat.endpoints.map((ep, idx) => (
                                <button
                                  key={idx}
                                  onClick={() => selectEndpoint(ep)}
                                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-colors ${
                                    selectedEndpoint === ep 
                                      ? 'bg-indigo-50 border border-indigo-200' 
                                      : 'hover:bg-slate-50'
                                  }`}
                                >
                                  <Badge className={`${METHOD_COLORS[ep.method]} text-[10px] px-1.5`}>
                                    {ep.method}
                                  </Badge>
                                  <span className="text-xs text-slate-700 truncate">
                                    {ep.summary}
                                  </span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* Main Content - Request/Response */}
          <div className="col-span-8 space-y-6">
            {selectedEndpoint ? (
              <>
                {/* Endpoint Info */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <Badge className={METHOD_COLORS[selectedEndpoint.method]}>
                        {selectedEndpoint.method}
                      </Badge>
                      <code className="text-sm bg-slate-100 px-2 py-1 rounded">
                        {selectedEndpoint.path}
                      </code>
                    </div>
                    <CardTitle className="mt-2">{selectedEndpoint.summary}</CardTitle>
                    <CardDescription>{selectedEndpoint.description}</CardDescription>
                  </CardHeader>
                  
                  {selectedEndpoint.parameters && (
                    <CardContent>
                      <h4 className="font-medium text-sm mb-2">Paramètres</h4>
                      <div className="space-y-2">
                        {selectedEndpoint.parameters.map((param, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm">
                            <code className="bg-slate-100 px-2 py-0.5 rounded">{param.name}</code>
                            <span className="text-slate-500">({param.type})</span>
                            <span className="text-slate-600">{param.description}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  )}
                </Card>

                {/* Request Builder */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Requête</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {selectedEndpoint.requestBody && (
                      <div>
                        <Label>Body (JSON)</Label>
                        <Textarea
                          value={requestBody}
                          onChange={(e) => setRequestBody(e.target.value)}
                          className="font-mono text-sm mt-1 h-48"
                          placeholder="{}"
                        />
                      </div>
                    )}
                    
                    <Button 
                      onClick={executeRequest} 
                      disabled={loading || !apiKey}
                      className="w-full"
                    >
                      {loading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      ) : (
                        <Play className="h-4 w-4 mr-2" />
                      )}
                      Exécuter la requête
                    </Button>
                  </CardContent>
                </Card>

                {/* Response */}
                {response && (
                  <Card>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg flex items-center gap-2">
                          Réponse
                          {response.isError ? (
                            <AlertCircle className="h-4 w-4 text-red-500" />
                          ) : (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          )}
                        </CardTitle>
                        <Badge className={response.isError ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}>
                          {response.status} {response.statusText}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="relative">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="absolute top-2 right-2"
                          onClick={() => copyToClipboard(JSON.stringify(response.data, null, 2))}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto text-sm">
                          {JSON.stringify(response.data, null, 2)}
                        </pre>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              /* Code Examples when no endpoint selected */
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="h-5 w-5 text-yellow-500" />
                    Démarrage rapide
                  </CardTitle>
                  <CardDescription>
                    Exemples de code pour intégrer l'API B2RCS
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Tabs value={selectedLanguage} onValueChange={setSelectedLanguage}>
                    <TabsList>
                      <TabsTrigger value="curl">cURL</TabsTrigger>
                      <TabsTrigger value="python">Python</TabsTrigger>
                      <TabsTrigger value="nodejs">Node.js</TabsTrigger>
                      <TabsTrigger value="php">PHP</TabsTrigger>
                    </TabsList>
                    
                    {Object.entries(CODE_EXAMPLES).map(([lang, code]) => (
                      <TabsContent key={lang} value={lang} className="relative">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="absolute top-2 right-2 z-10"
                          onClick={() => copyToClipboard(code)}
                        >
                          {copied ? <CheckCircle className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                        </Button>
                        <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto text-sm">
                          <code>{code}</code>
                        </pre>
                      </TabsContent>
                    ))}
                  </Tabs>
                </CardContent>
              </Card>
            )}

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-4">
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-indigo-100 rounded-lg">
                      <Send className="h-5 w-5 text-indigo-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">3072</p>
                      <p className="text-xs text-slate-500">Messages/batch max</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Shield className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">1000</p>
                      <p className="text-xs text-slate-500">Numéros/capability check</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="pt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <FileText className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">42+</p>
                      <p className="text-xs text-slate-500">Templates disponibles</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
  );

  if (embedded) {
    return content;
  }

  return <Layout>{content}</Layout>;
};

export default APIDeveloper;
